import React, { useState } from 'react';
import { SOURCE_META } from '../shared/intelligenceLog';
import { toEpoch } from './leadStatusSnapshot';

const VISIBLE_STEPS = 4;

const formatWhen = (value) => {
  const t = toEpoch(value);
  if (!t) return '';
  return new Date(t).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ORIGIN_LABEL = {
  baseline: 'Earlier snapshot',
  current: 'On file',
};

function ChangeRow({ change }) {
  const value = change.kind === 'added'
    ? change.to
    : change.kind === 'removed'
      ? change.from
      : `${change.from} → ${change.to}`;
  return (
    <div className={`ir-change ir-change--${change.kind}`}>
      <span className="ir-change-label">{change.label}</span>
      <span className="ir-change-value">{value}</span>
    </div>
  );
}

function RecordCard({ event, open, onToggle }) {
  const meta = SOURCE_META[event.source] || { label: event.source, icon: 'fa-circle', brand: false };
  const hasDelta = (event.changes && event.changes.length > 0)
    || (event.addedNotes && event.addedNotes.length > 0)
    || (event.removedNotes && event.removedNotes.length > 0);
  const previewFacts = (event.facts || []).slice(0, 3);
  const origin = ORIGIN_LABEL[event.origin];
  const when = formatWhen(event.capturedAt);

  return (
    <article className="ir-record-item">
      <div className="ir-record-rail" aria-hidden="true">
        <span className="ir-record-dot" />
        <span className="ir-record-line" />
      </div>
      <div
        role="button"
        tabIndex={0}
        className={`ir-record-card ${open ? 'is-open' : ''}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="ir-record-top">
          <span className="ir-record-source">
            <i className={`${meta.brand ? 'fab' : 'fa'} ${meta.icon}`} />
            {meta.label}
          </span>
          {when && <span className="ir-record-when">{when}</span>}
        </div>
        <p className="ir-record-headline">{event.headline}</p>
        {origin && <span className="ir-record-origin">{origin}</span>}
        {hasDelta && (
          <div className="ir-record-changes">
            {event.changes.map((change) => <ChangeRow key={change.key} change={change} />)}
            {event.addedNotes.map((note) => (
              <div key={`add-${note}`} className="ir-change ir-change--added">{note}</div>
            ))}
            {event.removedNotes.map((note) => (
              <div key={`rem-${note}`} className="ir-change ir-change--removed">{note}</div>
            ))}
          </div>
        )}
        {!hasDelta && previewFacts.length > 0 && (
          <p className="ir-record-preview">
            {previewFacts.map((item) => `${item.label} ${item.value}`).join(' · ')}
          </p>
        )}
        {open && (
          <div className="ir-record-detail">
            {event.facts?.length > 0 && (
              <ul className="ir-note-list">
                {event.facts.map((item) => (
                  <li key={item.key}><span className="ir-note-kicker">{item.label}</span> {item.value}</li>
                ))}
              </ul>
            )}
            {event.notes?.length > 0 && (
              <ul className="ir-note-list">
                {event.notes.map((note) => <li key={note}>{note}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function ActivityCard({ item, onOpenMail }) {
  const openable = item.kind === 'email' && !!(item.mailDocId || item.subject) && typeof onOpenMail === 'function';
  const open = () => { if (openable) onOpenMail(item); };
  const when = formatWhen(item.at);
  return (
    <article className="ir-record-item">
      <div className="ir-record-rail" aria-hidden="true">
        <span className={`ir-record-dot ir-record-dot--${item.kind}`} />
        <span className="ir-record-line" />
      </div>
      <div
        role={openable ? 'button' : undefined}
        tabIndex={openable ? 0 : undefined}
        title={openable ? 'Open the sent email' : undefined}
        aria-label={openable ? `Open sent email: ${item.subject || item.title}` : undefined}
        className={`ir-record-card ir-record-card--step ir-record-card--${item.kind}${openable ? ' ir-record-card--mail' : ''}`}
        onClick={openable ? open : undefined}
        onKeyDown={openable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
          }
        } : undefined}
      >
        <div className="ir-record-top">
          <span className="ir-record-source ir-record-source--step">
            <i className={item.icon.includes('fa-brands') ? item.icon : `fa ${item.icon}`} />
            {item.title}
          </span>
          {(openable || when) && (
            <span className="ir-record-when">
              {openable && <i className="fa fa-chevron-right ir-record-open" aria-hidden="true" />}
              {when}
            </span>
          )}
        </div>
        {item.detail && <p className="ir-record-preview">{item.detail}</p>}
        {item.from && <p className="ir-record-preview">From {item.from}</p>}
      </div>
    </article>
  );
}

const stepTime = (step) => (step.type === 'research'
  ? toEpoch(step.event.capturedAt)
  : toEpoch(step.item.at));

export default function CompanyRecord({ events, activity = [], status, openId, onToggle, onOpenMail }) {
  const [showAll, setShowAll] = useState(false);
  const steps = [
    ...events.map((event) => ({ type: 'research', key: `r-${event.id}`, event })),
    ...activity.map((item) => ({ type: 'activity', key: `a-${item.id}`, item })),
  ].sort((a, b) => {
    const ta = stepTime(a);
    const tb = stepTime(b);
    if (!ta || !tb) return (tb ? 1 : 0) - (ta ? 1 : 0);
    return tb - ta;
  });
  const visible = showAll ? steps : steps.slice(0, VISIBLE_STEPS);
  const hidden = steps.length - visible.length;

  return (
    <section className="ir-section">
      <div className="ir-section-label">
        <i className="fa fa-clock-rotate-left" />
        Company Record
        {steps.length > 0 && <span className="ir-section-count">{steps.length}</span>}
      </div>
      {steps.length === 0 ? (
        <div className="ir-empty ir-empty--record">
          <i className="fa fa-layer-group" />
          <span>
            {status === 'error'
              ? 'The record could not be loaded. Research you already saved still shows in the sections below.'
              : 'Every step on this contact collects here: import, research runs, emails, replies, calls and notes.'}
          </span>
        </div>
      ) : (
        <>
          <p className="ir-record-hint">
            Every step on this contact, newest first. Research runs stay side by side with what changed in between.
          </p>
          <div className="ir-record">
            {visible.map((step) => (step.type === 'research' ? (
              <RecordCard
                key={step.key}
                event={step.event}
                open={openId === step.event.id}
                onToggle={() => onToggle(step.event.id)}
              />
            ) : (
              <ActivityCard key={step.key} item={step.item} onOpenMail={onOpenMail} />
            )))}
          </div>
          {(hidden > 0 || showAll) && steps.length > VISIBLE_STEPS && (
            <button type="button" className="ir-record-more" onClick={() => setShowAll(!showAll)}>
              <i className={`fa fa-chevron-${showAll ? 'up' : 'down'}`} />
              {showAll ? 'Show fewer steps' : `Show ${hidden} earlier step${hidden === 1 ? '' : 's'}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
